import determineAccess, { AccessAction } from 'helpers/determineAccess'
import { useRouter } from 'next/router'
import styled from '@emotion/styled'
import Spinner from 'library/atoms/Spinner'
import Link from 'next/link'
import { LEGACY_ROUTES } from 'enums/routes'
import Flex from 'shared/library/atoms/Flex'
import NotifierErrorMessage from 'library/molecules/NotifierErrorMessage'
import Button from 'library/atoms/Button'
import useTranslation from 'next-translate/useTranslation'
import { useInstructor } from 'shared/state/useAuth'
import { ReactNode } from 'react'

const ShouldAccess = ({ children }: { children: ReactNode }) => {
  const { query, pathname } = useRouter()
  const { t } = useTranslation('common')

  const outletSlug = query?.outletSlug as string

  const { instructor, authLoading, authenticated } = useInstructor()

  const access = determineAccess({
    pathname,
    instructor,
    outletSlug,
    authLoading,
    authenticated
  })

  if (access?.action === AccessAction.indicateLoading) {
    return (
      <StyledPageWrapper>
        <Spinner />
      </StyledPageWrapper>
    )
  }

  if (access?.action === AccessAction.redirectToLogin) {
    window.location.assign(
      process.env.NEXT_PUBLIC_EOLA_BUSINESS_ROOT + 'instructors/sign_in'
    )
    return null
  }

  if (access?.action === AccessAction.indicateUnauthorised) {
    const firstOutletSlug = instructor?.instructors_outlets?.[0].outlet?.slug
    const href = firstOutletSlug
      ? LEGACY_ROUTES.OUTLET.url({ outletSlug: firstOutletSlug })
      : LEGACY_ROUTES.OUTLETS.url()
    return (
      <NotifierErrorMessage
        headingText={t('cannot-access')}
        subText={t('no-permission-to-access')}
      >
        <Flex justifyContent={'center'} width={'100%'}>
          <Link href={href} passHref>
            <Button as="a" variant="primary">
              {t('return-to-dashboard')}
            </Button>
          </Link>
        </Flex>
      </NotifierErrorMessage>
    )
  }

  return <>{children}</>
}

export default ShouldAccess

const StyledPageWrapper = styled.div(() => ({
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  alignItems: 'center'
}))
